'use client';

import { useTranslation } from 'react-i18next';

import { RadioGroup } from '@sola/ark-core/molecules/radio-group/radio-group.tsx';
import { Select } from '@sola/ark-core/molecules/select/select.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';

import { useDonate } from '../donate-context.tsx';
import { DonateActionEnum, DonateFrequency } from '../donate.types.ts';
import { useDonateActions } from '../hooks/use-donate-actions.tsx';

export interface DonateFrequencySelectorProps {
  className?: string;
  alt?: boolean;
}

export const DonateFrequencySelector: React.FC<
  DonateFrequencySelectorProps
> = ({ className, alt }) => {
  const { t } = useTranslation(['donations']);
  const { state, config, dispatch } = useDonate();
  const { handleFrequencyChange } = useDonateActions();

  const frequencyItems = config.enabledFrequencies.map((frequency) => ({
    value: frequency.toString(),
    text: `${t('donations:frequency', { context: frequency })}`,
    disabled: false,
  }));

  if (!config.defaultFrequency || frequencyItems.length <= 1) {
    return null;
  }

  return (
    <div
      className={cn(
        `DonateFrequencySelector ${className || ''}`,
        alt ? 'flex-1' : 'flex w-full justify-center items-center',
      )}
    >
      {alt ? (
        <Select
          name="donateFrequencyDropdown"
          items={frequencyItems}
          value={state.donateFrequency}
          label={t('donations:frequency')}
          aria-label={t('donations:frequency')}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            dispatch({
              type: DonateActionEnum.SET_FREQUENCY,
              payload: e.target.value as DonateFrequency,
            });
            dispatch({
              type: DonateActionEnum.SET_OTHER_SELECTED,
              payload: true,
            });
          }}
        />
      ) : (
        <RadioGroup
          key={`${state.donateFrequency}-frequency`}
          aria-label={t('donations:donateFrequency')}
          name="donateFrequency"
          items={frequencyItems}
          orientation="horizontal"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleFrequencyChange(e.target.value as DonateFrequency)
          }
          value={state.donateFrequency}
        />
      )}
    </div>
  );
};
